import { Entity } from "@/gql/types";
import moment from "moment";

export const L0_CODES = {
  HR: 1,
  FINANCE: 1,
  LEGAL: 1,
  BUSINESS: 1,
  GOVERNANCE: 1,
  OTHER: 1,
};

export interface InfoTypePolicy {
  id: string;
  name: string;
  desc: string;
}

export interface ContextLabel {
  id: string;
  labelCategory:
    | "Cluster Age"
    | "Cluster Activity"
    | "Cluster Exposure"
    | "Cluster Sprawl"
    | "Department"
    | "Entity";
  labelType: "Cluster_label" | "";
  labelName: string;
}

export interface FoldersContextLabel {
  id: string;
  desc: string;
  origin: "Manual" | "Backoffice" | "Runtime";
  sensitivity: number; // 0.2 0.5 0.85
  sensitivityLevel: number; // 1 2 3
  sensitivityType: InformationSensitivityType;
  labelCategory:
    | "Intellectual property"
    | "Projects"
    | "Operations & Other"
    | "Board & Management"
    | "Product & R&D"
    | "Sales & Marketing";
  labelType: "Folders_context";
  labelName: string;
}

export const FoldersContextLabelHelper = {
  get_ctx_from_folder: (
    folder: Entity
  ): { id: string; name: string } | null => {
    if (folder.labels?.length === 0) {
      return null;
    }
    const ctx_labels = folder.labels?.filter(
      (l: any) => l.labelType === "Folder_context"
    )!;
    if (ctx_labels?.length > 0) {
      const { id, name } = ctx_labels[0]!;
      return {
        id: id!,
        name: name!,
      };
    } else {
      return null;
    }
  },
};

export type InformationSensitivityType = "Internal" | "External" | "Both";

const ContextLabelsCategoryMap: Record<string, true> = {
  "Cluster Age": true,
  "Cluster Activity": true,
  "Cluster Exposure": true,
  "Cluster Sprawl": true,
  Department: true,
  Entity: true,
  Folder_context: true,
  "Data Room Folders Information": true,
};

export interface InformationType {
  id: string;
  // for regulated info type only
  content_code?: number;
  code: number;
  labelName: string;
  shortName: string;
  sensitivity: number;
  origin: "Manual" | "Backoffice" | "Runtime";
  sensitivityType: InformationSensitivityType;
  sensitivityTypeName: string;
  sensitivityLevel: number;
  displaySensitivityLevel?: number;
  regulationType: "None" | "Privacy" | "Hippa" | "Sox";
  desc?: string;
  description?: string;
  name?: string;
  concrete: 0 | 1;
  concreteScore: 0 | 1;
  l0Order?: number;
  parentId?: string;
  has_children?: boolean;
  parentName?: string;
  isCustom?: boolean;
  generation: number;
  confidence: number;
  relatedTo: string;
  // virtual label property
  infoTypeCategoryId: string;
  labelCategory:
    | "Custom"
    | "Content"
    | "Virtual"
    | "hasMipVirtual"
    | "Purview Labels/Automated"
    | "Internal Information"
    | "External Information"
    | "Regulations";

  labelType:
    | "Custom_label"
    | "Content_label"
    | "Adhoc"
    | "Virtual_label"
    | "Content_abstract";
  abstracts?: string[];
  policySection: {
    alertReceivers: string[];
    policies: {
      riskyUsersPolicyId?: string;
      dataLeaksPolicyId?: string;
      lackOfLabelingPolicyId?: string;
      dataLossPolicyId?: string;
      retentionPolicyId?: string;
      detectPolicyId?: string;
      contentTypePolicyId?: string;
      documentsEncryptionPolicyId?: string;
      restrictionPolicyId?: string;
    };
  };
  active_policies_display_names?: Array<{
    desc: string;
    name: string;
    id: string;
  }>;
}

export interface InformationTypeTree extends InformationType {
  nested: InformationTypeTree[];
}

export const InfoTypeHelper = {
  REGULATED_ID: () => "a9383d32-7694-4d84-b37c-79afb945505b",
  REGULATED_CODE: () => 7,
  DEFAULT_POLICIES_SECTION: () => {
    return {
      alertReceivers: [],
      policies: {},
    };
  },
  get_policy: (policyKey: string) => (info_type: any): string => {
    return info_type?.policySection?.[policyKey]?.policyValue?.id || "";
  },
  l0_name_by_code: (code: number): string => {
    if (code > 0 && code <= 6) {
      return ["", "Other", "Governance", "Business", "Finance", "HR", "Legal"][
        code
      ];
    } else {
      return "?";
    }
  },
  get_label_from_item: (item: any) => {
    return (item.labels || [])
      .filter((l: any) => l.type === "Content" || l.type === "Custom")
      .sort((b: any, a: any) => b.generation - a.generation)[0];
  },
  canShowConfidence: (() => {
    const time = moment("20210602", "YYYYMMDD");
    return (item: any) => {
      const { infoLabel } = item;
      if (infoLabel) {
        return false;
      }
      if (infoLabel && infoLabel.generation < 2 && infoLabel.confidence > 0) {
        return true;
      }
      if (item.createdAt) {
        // is item was scanned after 2 jun 2021
        return time.isBefore(item.createdAt);
      } else {
        return false;
      }
    };
  })(),
  isEqualInfoTypeId: (id1: string, id2: string) =>
    id1.toLowerCase() == id2.toLowerCase(),
  isEqual: (t1: InformationType, t2: InformationType) => {
    return InfoTypeHelper.isEqualInfoTypeId(t1.id, t2.id);
  },
  getName: (label: InformationType): string => {
    if (!label) {
      return "";
    }
    return label.name || label.labelName || label.shortName;
  },
  getMostRelevantLabel: (
    labels: InformationType[],
    default_label: InformationType
  ): InformationType => {
    if (!labels || labels.length === 0) {
      return default_label;
    }
    if (labels.length === 1) {
      return labels[0];
    }
    const sorted_labels = labels
      .filter(
        // @ts-ignore
        (s) =>
          s.labelCategory === "Content" ||
          // @ts-ignore
          s.type === "Content" ||
          // @ts-ignore
          s.labelType === "Adhoc"
      )
      .sort((a, b) => b.generation - a.generation);

    const label = sorted_labels[0];
    return label;
  },
  get_most_relevant_label: (
    labels: InformationType[]
  ): InformationType | undefined => {
    if (!labels || labels.length === 0) {
      return undefined;
    }
    if (labels.length === 1) {
      // @ts-ignore
      return labels[0];
    }
    const sorted_labels = labels
      .filter(
        // @ts-ignore
        (s) =>
          s.labelCategory === "Content" ||
          // @ts-ignore
          s.type === "Content"
      )
      .sort((a, b) => b.generation - a.generation);
    return sorted_labels[0];
  },
  get_most_relevant_label_with_parents(labels: InformationType[]) {
    const label = InfoTypeHelper.get_most_relevant_label(labels)!;
    // @ts-ignore
    const all = label.path
      // @ts-ignore
      .map((l) => labels.find((d) => d.id == l).name)
      .slice(0, -1);
    return {
      ...label,
      tree: all,
    };
  },
  getLabelName: (labels: InformationType[]): string => {
    const label = InfoTypeHelper.get_most_relevant_label(labels);
    return label ? label.labelName || (label.name as string) : "?";
  },
  getContextLabels: (labels: any[]): ContextLabel[] => {
    // @DEMO_PATCH
    labels = labels.map((d: any) => {
      if (d.labelCategory === "Department") {
        return {
          ...d,
          concreteScore: 1,
        };
      }
      return d;
    });
    const ctx_labels = labels.filter((d: any) =>
      ["Cluster_label", "Entity_label", "Folder_context"].includes(d.labelType)
    );
    return ctx_labels.map((d) => {
      return {
        ...d,
        post_fix:
          d.labelCategory === "Department"
            ? "Departments info"
            : d.labelCategory === "Folder_context"
            ? "Folders' info"
            : "",
      };
    });
  },
  getContextName: (labels: any[], name_prop = "name"): string => {
    labels = labels.map((d) => {
      if (d.type === "Department") {
        return {
          ...d,
          concreteScore: 1,
        };
      }
      return d;
    });
    const ctx_labels = labels.filter(
      (d: any) => d.type in ContextLabelsCategoryMap
    );
    console.log("getContextName ctx_labels : labels.length", ctx_labels);
    if (ctx_labels.length === 0) {
      return "";
    } else {
      return ctx_labels.sort((a, b) => b.concreteScore - a.concreteScore)[0][
        name_prop
      ];
    }
  },
  getL0Order(code: number): number {
    // financial legal hr governance business other
    switch (code) {
      case 1: // Other
        return 7;
      case 2: // Governance
        return 4;
      case 3: // Business
        return 5;
      case 4: // Finance
        return 1;
      case 5: // HR
        return 3;
      case 6: // Legal
        return 2;
      case 7:
      default:
        return 6;
    }
  },
  getL0CodeByOrder(order: number): number {
    // financial legal hr governance business other
    switch (order) {
      case 1:
        return 4; // Finance
      case 2:
        return 6; // Legal
      case 3:
        return 5; // HR
      case 4:
        return 2; //Governance
      case 5:
        return 3; // Business
      case 6:
        return 7;
      case 7:
      default:
        return 1;
    }
  },
  colorByL0Code: (code: number): string => {
    switch (code) {
      case 1: // Other
        return "--code1";
      case 2: // Governance
        return "--code2";
      case 3: // Important
        return "--code3";
      case 4: // Finance
        return "--code4";
      case 5: // HR
        return "--code5";
      case 6: // Legal
      default:
        return "--code6";
    }
  },
};
